import { fetchSize } from "@/config/constants";

export interface PagedResponse<Item = any> {
  content?: Item[] | null;
  totalElements?: number;
  totalPages?: number;
  [key: string]: any;
}

export type PagedFetcher<Item = any> = (page: number, size: number) => Promise<PagedResponse<Item>>;

function extractContent<Item>(response: PagedResponse<Item> | undefined | null): Item[] {
  return Array.isArray(response?.content) ? response.content : [];
}

// Fetches page 0 once, then fetches the remaining pages in parallel.
// If onFirstPage is given, it's called with page 0 first (paint 1) before the
// full merged result is returned (paint 2).
export async function fetchAll<Item = any>(
  fetcher: PagedFetcher<Item>,
  size: number = fetchSize.default,
  onFirstPage?: (firstResponse: PagedResponse<Item>) => void
): Promise<PagedResponse<Item>> {
  const firstResponse = await fetcher(0, size);
  onFirstPage?.(firstResponse);

  if (!firstResponse || typeof firstResponse !== "object") {
    return firstResponse;
  }

  const firstPage = extractContent<Item>(firstResponse);
  const totalElements = Number(firstResponse.totalElements ?? firstPage.length);

  if (!Number.isFinite(totalElements) || totalElements <= firstPage.length) {
    return firstResponse;
  }

  const remainingPageCount = Math.ceil((totalElements - firstPage.length) / size);
  const remainingPageNumbers = Array.from({ length: remainingPageCount }, (_, index) => index + 1);
  const remainingResponses = await Promise.all(remainingPageNumbers.map((pageNum) => fetcher(pageNum, size)));
  const fullContent = remainingResponses.reduce<Item[]>(
    (merged, response) => merged.concat(extractContent(response)),
    [...firstPage]
  );

  return { ...firstResponse, content: fullContent, totalElements };
}
